import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { IncentiveHeader } from '../../components/Incentive/IncentiveHeader';
import { IncentivePreviewCard } from '../../components/Incentive/IncentivePreviewCard';
import { IncentiveSummaryCard } from '../../components/Incentive/IncentiveSummaryCard';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchIncentiveDetails, addIncentive, editIncentive } from '../../store/incentiveSlice';
import { fetchDistributorChannels } from '../../store/distributorChannelSlice';
import type { CreateIncentiveForm, UnitType } from '../../types/incentive';
import { getZonesAPI, getCitiesAPI } from '../../api/locations';
import { getProducts, type ProductItem } from '../../api/products';
import {
  downloadIncentiveTemplateAPI,
  uploadIncentivesBulkAPI,
} from '../../api/incentive';
import { CustomDropdown } from '../../components/Common/CustomDropdown';
import { CustomCalendar } from '../../components/Common/CustomCalendar';

export const CreateIncentive: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, selectedIncentiveDetails } = useAppSelector((state) => state.incentive);
  const { channels } = useAppSelector((state) => state.channels);

  const [zones, setZones] = useState<Array<{ id: number; name: string }>>([]);
  const [cities, setCities] = useState<Array<{ id: number; zoneId?: number; name: string }>>([]);
  const [productsList, setProductsList] = useState<ProductItem[]>([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkToast, setBulkToast] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateIncentiveForm>(() => ({
    zone: '',
    zoneId: '',
    city: '',
    cityId: '',
    productId: '',
    productName: '',
    incentiveName: '',
    distributorType: 'GT MT',
    unitType: 'Litre (L)',
    incentiveRate: '1',
    unitThreshold: '1L',
    targetQuantity: '250',
    payoutType: 'Cash',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'Active',
    isDelete: false,
  }));

  const [crateMapping, setCrateMapping] = useState('....Units / Crate');

  const filteredCities = useMemo(() => {
    if (!formData.zoneId) return cities;
    const targetZoneId = String(formData.zoneId);

    return cities.filter((c: any) => {
      const cZoneId = String(c.zoneId ?? c.zone_id ?? c.territory?.zoneId ?? '');
      return cZoneId === targetZoneId;
    });
  }, [cities, formData.zoneId]);

  useEffect(() => {
    if (!channels || channels.length === 0) {
      dispatch(fetchDistributorChannels());
    }
    const loadDropdowns = async () => {
      try {
        const [zonesRes, citiesRes, prodRes] = await Promise.allSettled([
          getZonesAPI(),
          getCitiesAPI(),
          getProducts({ limit: 100 }),
        ]);

        if (zonesRes.status === 'fulfilled' && zonesRes.value?.data) {
          setZones(zonesRes.value.data);
        }
        if (citiesRes.status === 'fulfilled' && citiesRes.value?.data) {
          setCities(citiesRes.value.data);
        }
        if (prodRes.status === 'fulfilled' && prodRes.value?.data?.products) {
          setProductsList(prodRes.value.data.products);
        }
      } catch (err) {
        console.warn('Error loading dropdowns for incentive creation:', err);
      }
    };
    loadDropdowns();
  }, [dispatch, channels]);

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchIncentiveDetails(id));
    }
  }, [isEditMode, id, dispatch]);

  useEffect(() => {
    queueMicrotask(() => {
      if (isEditMode && selectedIncentiveDetails) {
        const raw: any = selectedIncentiveDetails.rawBackendData || {};
        const rawCities = selectedIncentiveDetails.citiesList || [];
        const filteredInitCities = rawCities.filter((c) => c !== 'All Cities');

        let sDate = selectedIncentiveDetails.startDate || raw.startDate || '';
        if (sDate && sDate.includes('T')) sDate = sDate.split('T')[0];
        let eDate = selectedIncentiveDetails.endDate || raw.endDate || '';
        if (eDate && eDate.includes('T')) eDate = eDate.split('T')[0];

        let distTypeName = raw.distributorTypeName || selectedIncentiveDetails.distType;
        if ((!distTypeName || distTypeName === 'GT MT' || distTypeName === '-') && channels && channels.length > 0 && (raw.distributorTypeId || selectedIncentiveDetails.distributorTypeId)) {
          const targetId = Number(raw.distributorTypeId || selectedIncentiveDetails.distributorTypeId);
          const found: any = channels.find((c: any) => Number(c.id) === targetId);
          if (found) distTypeName = found.name || found.channelName;
        }

        let initCityIds = (selectedIncentiveDetails.cityIds && selectedIncentiveDetails.cityIds.length > 0
          ? selectedIncentiveDetails.cityIds
          : selectedIncentiveDetails.cityId
          ? [Number(selectedIncentiveDetails.cityId)]
          : []
        ).filter((n: any) => n !== null && n !== undefined && !isNaN(Number(n)) && Number(n) > 0);

        if (initCityIds.length === 0 && cities.length > 0) {
          const namesToMatch = filteredInitCities.length > 0 ? filteredInitCities : (selectedIncentiveDetails.city ? selectedIncentiveDetails.city.split(', ') : []);
          const matched = cities.filter((c) => namesToMatch.includes(c.name));
          if (matched.length > 0) {
            initCityIds = matched.map((c) => Number(c.id));
          }
        }

        const isInactive = raw.isDelete === true || raw.isDelete === 1 || raw.is_delete === true || raw.is_delete === 1 || String(selectedIncentiveDetails.status).toLowerCase() === 'inactive';

        setFormData({
          id: selectedIncentiveDetails.id,
          code: selectedIncentiveDetails.code || selectedIncentiveDetails.incentiveCode,
          incentiveCode: selectedIncentiveDetails.incentiveCode || selectedIncentiveDetails.code || selectedIncentiveDetails.id,
          zone: selectedIncentiveDetails.zone || raw.zoneName || '',
          zoneId: raw.zoneId || selectedIncentiveDetails.zoneId || '',
          city: selectedIncentiveDetails.city || raw.cityName || filteredInitCities.join(', '),
          cityId: initCityIds.length > 0 ? (initCityIds.length === 1 ? initCityIds[0] : (initCityIds as any)) : (raw.cityId || selectedIncentiveDetails.cityId || ''),
          cityIds: initCityIds,
          productId: String(selectedIncentiveDetails.productId || raw.productId || ''),
          productName: selectedIncentiveDetails.product || raw.productName || '',
          incentiveName: selectedIncentiveDetails.name || raw.name || '',
          distributorType: distTypeName || 'testing channel',
          distributorTypeId: raw.distributorTypeId || selectedIncentiveDetails.distributorTypeId,
          unitType: selectedIncentiveDetails.unitType || 'Litre (L)',
          incentiveRate: String(selectedIncentiveDetails.rewardAmount ?? raw.rewardValue ?? '1'),
          unitThreshold: selectedIncentiveDetails.unitThreshold || '1L',
          targetQuantity: String(selectedIncentiveDetails.targetQuantity ?? raw.targetQuantity ?? '250'),
          payoutType: selectedIncentiveDetails.payout || raw.rewardType || 'Cash',
          startDate: sDate,
          endDate: eDate,
          description: selectedIncentiveDetails.description || raw.description,
          status: isInactive ? 'Inactive' : 'Active',
          isDelete: isInactive,
        });

        if (selectedIncentiveDetails.crateMapping) {
          setCrateMapping(selectedIncentiveDetails.crateMapping);
        }
      }
    });
  }, [isEditMode, selectedIncentiveDetails, channels, cities]);

  useEffect(() => {
    if (cities.length > 0 && (!formData.cityIds || formData.cityIds.length === 0) && formData.city) {
      const cityNames = (typeof formData.city === 'string' ? formData.city.split(', ') : []).map((s) => s.trim()).filter(Boolean);
      const matched = cities.filter((c) => cityNames.includes(c.name));
      if (matched.length > 0) {
        const ids = matched.map((c) => Number(c.id));
        setFormData((prev) => ({
          ...prev,
          cityIds: ids,
          cityId: ids.length === 1 ? ids[0] : (ids as any),
        }));
      }
    }
  }, [cities, formData.city, formData.cityIds]);

  const handleInputChange = (
    field: keyof CreateIncentiveForm,
    value: string | number
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const selectedCityNames = useMemo(() => {
    if (Array.isArray(formData.city)) return formData.city;
    if (typeof formData.city === 'string' && formData.city) {
      return formData.city.split(', ').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }, [formData.city]);

  const handleMultiCityChange = (selectedNames: string[]) => {
    const actualNames = selectedNames.filter((n) => !n.startsWith('Select All') && !n.startsWith('Select all'));
    const matchedCityObjects = cities.filter((c) => actualNames.includes(c.name));
    const cityIds = matchedCityObjects.map((c) => Number(c.id));

    setFormData((prev) => ({
      ...prev,
      city: actualNames.join(', '),
      cityId: cityIds.length > 0 ? (cityIds.length === 1 ? cityIds[0] : (cityIds as any)) : '',
      cityIds,
    }));
  };

  const distributorTypeOptions = useMemo(() => {
    if (channels && channels.length > 0) {
      const channelNames = channels.map((c: any) => c.name || c.channelName || String(c)).filter(Boolean);
      return ['Select All Types', ...Array.from(new Set(channelNames))];
    }
    const defaultTypes = ['General Trade (GT)', 'Modern Trade (MT)', 'Distributor + Dealer', 'testing channel'];
    return ['Select All Types', ...defaultTypes];
  }, [channels]);

  const selectedDistTypeNames = useMemo(() => {
    if (Array.isArray(formData.distributorType)) return formData.distributorType as string[];
    if (typeof formData.distributorType === 'string' && formData.distributorType) {
      return formData.distributorType.split(', ').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }, [formData.distributorType]);

  const handleMultiDistTypeChange = (selectedNames: string[]) => {
    const actualNames = selectedNames.filter((n) => !n.startsWith('Select All') && !n.startsWith('Select all'));

    let matchedTypeId: number | undefined;
    if (channels && channels.length > 0) {
      const foundCh = channels.find((c: any) => actualNames.includes(c.name || c.channelName));
      if (foundCh) matchedTypeId = Number(foundCh.id);
    }
    if (!matchedTypeId) {
      if (actualNames.includes('General Trade (GT)') || actualNames.includes('GT')) matchedTypeId = 1;
      else if (actualNames.includes('Modern Trade (MT)') || actualNames.includes('MT')) matchedTypeId = 2;
      else if (actualNames.includes('Distributor + Dealer') || actualNames.includes('testing channel')) matchedTypeId = 3;
    }

    setFormData((prev) => ({
      ...prev,
      distributorType: actualNames.join(', '),
      distributorTypeId: matchedTypeId || prev.distributorTypeId || 3,
    }));
  };

  const handleZoneChange = (zoneIdStr: string) => {
    const selectedZoneObj = zones.find((z) => String(z.id) === zoneIdStr);
    setFormData((prev) => ({
      ...prev,
      zoneId: zoneIdStr,
      zone: selectedZoneObj ? selectedZoneObj.name : '',
      city: '',
      cityId: '',
      cityIds: [],
    }));
  };

  const handleProductSelectByName = (productNameStr: string) => {
    if (productNameStr === 'Select a product' || productNameStr === 'All Products' || !productNameStr) {
      setCrateMapping('12 Units / Crate');
      setFormData((prev) => ({
        ...prev,
        productId: '',
        productName: 'Select a product',
        crateUnits: undefined,
      }));
      return;
    }

    const selectedProd = productsList.find((p) => p.name === productNameStr);
    const productIdStr = selectedProd ? String(selectedProd.id) : '';
    const name = selectedProd ? selectedProd.name : productNameStr;

    const units = selectedProd?.units_per_case ?? selectedProd?.crate?.units ?? 12;
    const mapping = `${units} Units / Crate`;

    setCrateMapping(mapping);
    setFormData((prev) => ({
      ...prev,
      productId: productIdStr,
      productName: name,
      crateUnits: units,
    }));
  };

  const handleBulkUpload = async (file: File) => {
    try {
      setBulkUploading(true);
      const res = await uploadIncentivesBulkAPI(file);
      const msg = res?.message || (res?.status === 'success' ? `Bulk targets uploaded successfully from "${file.name}"!` : `Uploaded "${file.name}" successfully!`);
      setBulkToast(msg);
      setTimeout(() => setBulkToast(null), 4000);
    } catch (err: any) {
      alert(err?.message || 'Failed to upload bulk incentive target file.');
    } finally {
      setBulkUploading(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!formData.incentiveName.trim()) {
      alert('Please enter an Incentive Name');
      return;
    }

    const codeOrId = formData.incentiveCode || formData.code || formData.id || id;

    let resolvedCityIds: number[] = [];
    if (Array.isArray(formData.cityIds) && formData.cityIds.length > 0) {
      resolvedCityIds = formData.cityIds.map(Number).filter((n) => !isNaN(n) && n > 0);
    }
    if (resolvedCityIds.length === 0 && formData.city) {
      const cityNames = (typeof formData.city === 'string' ? formData.city.split(', ') : []).map((s) => s.trim()).filter(Boolean);
      const matched = cities.filter((c) => cityNames.includes(c.name));
      resolvedCityIds = matched.map((c) => Number(c.id));
    }
    if (resolvedCityIds.length === 0 && formData.cityId && !isNaN(Number(formData.cityId)) && Number(formData.cityId) > 0) {
      resolvedCityIds = [Number(formData.cityId)];
    }
    if (resolvedCityIds.length === 0 && filteredCities.length > 0) {
      resolvedCityIds = [Number(filteredCities[0].id)];
    }
    if (resolvedCityIds.length === 0 && cities.length > 0) {
      resolvedCityIds = [Number(cities[0].id)];
    }
    if (resolvedCityIds.length === 0) {
      resolvedCityIds = [1];
    }

    const isInactive = formData.status === 'Inactive' || formData.isDelete === true || (formData as any).is_delete === true;

    const submitData: CreateIncentiveForm = {
      ...formData,
      id: codeOrId,
      code: codeOrId,
      incentiveCode: codeOrId,
      cityIds: resolvedCityIds,
      cityId: resolvedCityIds.length === 1 ? resolvedCityIds[0] : (resolvedCityIds as any),
      isDelete: isInactive,
      status: isInactive ? 'Inactive' : 'Active',
    };

    try {
      if (isEditMode) {
        await dispatch(editIncentive(submitData)).unwrap();
      } else {
        await dispatch(addIncentive(submitData)).unwrap();
      }
      navigate('/incentive');
    } catch (err: any) {
      alert(err || 'Failed to save incentive');
    }
  };

  return (
    <div className="min-h-screen pb-12">
      {/* Toast Notification */}
      {bulkToast && (
        <div className="fixed top-5 right-5 z-50 bg-slate-900 text-white px-4 py-3 rounded-2xl shadow-xl flex items-center gap-2 border border-slate-700 animate-in fade-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          <span className="text-xs font-semibold">{bulkToast}</span>
        </div>
      )}

      {/* Top Header Bar */}
      <IncentiveHeader
        title={isEditMode ? 'Edit Incentive' : 'Create New Incentive'}
        showBack={true}
        showActions={false}
        onBackClick={() => navigate('/incentive')}
      />
      <p className="text-xs text-gray-500 font-medium -mt-4 mb-6">
        Establish fresh rate criteria and volumetric targets for select products & distributors.
      </p>

      {/* Warning Banner for Active Incentive Modification */}
      {isEditMode && (
        <div className="mb-6 bg-red-50 border border-red-200/60 rounded-2xl p-4 flex items-start gap-3 text-xs md:text-sm">
          <div className="text-red-500 mt-0.5 shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <div className="font-bold text-[#B71C1C] text-xs md:text-sm">
              Active Incentive Modification
            </div>
            <div className="text-gray-600 text-xs mt-0.5">
              Changes to an active scheme may affect ongoing distributor eligibility and current inventory allocations.
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Grid Row 1: Incentive Details (Left 2 cols) + Bulk Target Assignment (Right 1 col) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Card 1: Incentive Details */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-0.5">
              Incentive Details
            </h2>
            <p className="text-xs text-gray-400 font-medium mb-5">
              Basic information and target products.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Zone */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Zone
                </label>
                <div className="relative">
                  <select
                    value={formData.zoneId || ''}
                    onChange={(e) => handleZoneChange(e.target.value)}
                    className="w-full bg-white border border-gray-300 focus:border-[#B71C1C] focus:ring-1 focus:ring-[#B71C1C] outline-none text-gray-700 text-xs md:text-sm rounded-xl py-2.5 px-3 pr-8 appearance-none transition-colors cursor-pointer"
                  >
                    <option value="">Select a zone</option>
                    {zones.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* City * */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  City <span className="text-red-500">*</span>
                </label>
                <CustomDropdown
                  placeholder="Select cities"
                  options={['Select All Cities', ...filteredCities.map((c) => c.name)]}
                  value={selectedCityNames}
                  onChange={handleMultiCityChange}
                  isMulti={true}
                  searchable={true}
                  showOkButton={true}
                  unitLabel="Cities"
                />
              </div>

              {/* Product * */}
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="block text-xs font-semibold text-gray-700">
                    Product <span className="text-red-500">*</span>
                  </span>
                  <span className="bg-[#1E88E5] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                    {crateMapping && crateMapping !== '....Units / Crate' ? crateMapping : 'AUTO-CRATE MAPPING'}
                  </span>
                </div>
                <CustomDropdown
                  placeholder="Select a product"
                  options={['Select a product', ...productsList.map((p) => p.name)]}
                  value={formData.productName || 'Select a product'}
                  onChange={handleProductSelectByName}
                  searchable={true}
                  isMulti={false}
                />
              </div>

              {/* Incentive Name * */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Incentive Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Summer Monsoon Bonanza"
                  value={formData.incentiveName}
                  onChange={(e) => handleInputChange('incentiveName', e.target.value)}
                  required
                  className="w-full bg-white border border-gray-300 focus:border-[#B71C1C] focus:ring-1 focus:ring-[#B71C1C] outline-none text-gray-700 text-xs md:text-sm rounded-xl py-2.5 px-3 transition-colors"
                />
              </div>

              {/* Distributor type */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Distributor type
                </label>
                <CustomDropdown
                  placeholder="Select distributor types"
                  options={distributorTypeOptions}
                  value={selectedDistTypeNames}
                  onChange={handleMultiDistTypeChange}
                  isMulti={true}
                  searchable={true}
                  showOkButton={true}
                  unitLabel="Types"
                />
              </div>
            </div>
          </div>

          {/* Card 2: Bulk Target Assignment */}
          <div className="lg:col-span-1">
            <IncentivePreviewCard
              isUploading={bulkUploading}
              onFileUpload={handleBulkUpload}
              onDownloadTemplate={async () => {
                try {
                  await downloadIncentiveTemplateAPI();
                } catch (err: any) {
                  console.warn('Backend download template failed:', err);
                }
              }}
            />
          </div>
        </div>

        {/* Grid Row 2: Benefit Structure & Validity & Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Card 3: Benefit Structure */}
          <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm">
            <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4">
              Benefit Structure
            </h3>

            <div className="space-y-4">
              {/* Unit Type * */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Unit Type <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {(['Litre (L)', 'Kilogram (KG)'] as UnitType[]).map((ut) => {
                    const isSelected = formData.unitType === ut;
                    return (
                      <button
                        key={ut}
                        type="button"
                        onClick={() => handleInputChange('unitType', ut)}
                        className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-rose-50 text-[#B71C1C] border-[#B71C1C]'
                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {ut}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Incentive Rate (per Unit) * & Unit Threshold */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Incentive Rate (per Unit) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center border border-gray-300 focus-within:border-[#B71C1C] focus-within:ring-1 focus-within:ring-[#B71C1C] rounded-xl overflow-hidden bg-white">
                    <span className="pl-3 text-xs text-gray-500 font-bold">₹</span>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      placeholder="1"
                      value={formData.incentiveRate}
                      onChange={(e) => handleInputChange('incentiveRate', e.target.value)}
                      required
                      className="w-full py-2.5 px-2 text-xs md:text-sm text-gray-800 outline-none bg-transparent font-semibold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Unit Threshold
                  </label>
                  <input
                    type="text"
                    placeholder="1L"
                    value={formData.unitThreshold}
                    onChange={(e) => handleInputChange('unitThreshold', e.target.value)}
                    className="w-full bg-white border border-gray-300 focus:border-[#B71C1C] focus:ring-1 focus:ring-[#B71C1C] outline-none text-gray-700 text-xs md:text-sm rounded-xl py-2.5 px-3 transition-colors font-semibold"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Card 4: Validity & Status */}
          <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm">
            <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4">
              Validity & Status
            </h3>

            <div className="space-y-4">
              {/* Start Date * */}
              <div>
                <CustomCalendar
                  label="Start Date"
                  required={true}
                  value={formData.startDate}
                  onChange={(dateStr) => handleInputChange('startDate', dateStr)}
                  disablePast={false}
                />
              </div>

              {/* End Date * */}
              <div>
                <CustomCalendar
                  label="End Date"
                  required={true}
                  value={formData.endDate}
                  onChange={(dateStr) => handleInputChange('endDate', dateStr)}
                  disablePast={false}
                />
              </div>

              {/* Status (Active / Inactive) - Only shown when editing */}
              {isEditMode && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Program Status <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange('status', 'Active');
                        handleInputChange('isDelete', false as any);
                      }}
                      className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold border transition-all cursor-pointer flex items-center justify-center gap-2 ${
                        !formData.isDelete && String(formData.status).toLowerCase() !== 'inactive'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-500 shadow-xs'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${
                          !formData.isDelete && String(formData.status).toLowerCase() !== 'inactive'
                            ? 'bg-emerald-500'
                            : 'bg-gray-400'
                        }`}
                      />
                      <span>Active</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange('status', 'Inactive');
                        handleInputChange('isDelete', true as any);
                      }}
                      className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold border transition-all cursor-pointer flex items-center justify-center gap-2 ${
                        Boolean(formData.isDelete) || String(formData.status).toLowerCase() === 'inactive'
                          ? 'bg-rose-50 text-[#B71C1C] border-[#B71C1C] shadow-xs'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${
                          Boolean(formData.isDelete) || String(formData.status).toLowerCase() === 'inactive'
                            ? 'bg-[#B71C1C]'
                            : 'bg-gray-400'
                        }`}
                      />
                      <span>Inactive</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Card 5: Bottom Incentive Summary & Actions */}
        <IncentiveSummaryCard
          productName={formData.productName}
          incentiveRate={formData.incentiveRate}
          unitThreshold={formData.unitThreshold}
          unitType={formData.unitType}
          startDate={formData.startDate}
          city={formData.city}
          citiesList={selectedCityNames}
          onRemoveCity={(cityNameToRemove) => {
            const nextCities = selectedCityNames.filter((c) => c !== cityNameToRemove);
            handleMultiCityChange(nextCities);
          }}
          onCancel={() => navigate('/incentive')}
          onSubmit={handleSubmit}
          isSubmitting={loading}
          submitButtonText={isEditMode ? 'Save Changes' : 'Create Scheme'}
        />
      </form>
    </div>
  );
};
